import React, { useMemo } from "react";
import styles from "./VariantChipSelector.module.scss";

interface VariantChipSelectorProps {
  label: string;
  allOptions: Array<string | number>;
  availableOptions: Array<string | number>;
  selectedValue: string | number | undefined;
  /** Called when any chip is clicked. isAvailable indicates if option is compatible with current filters. */
  onSelect: (value: string | number, isAvailable: boolean) => void;
}

export const VariantChipSelector: React.FC<VariantChipSelectorProps> = ({
  label,
  allOptions,
  availableOptions,
  selectedValue,
  onSelect,
}) => {
  const availableSet = useMemo(
    () => new Set(availableOptions.map(String)),
    [availableOptions],
  );

  return (
    <div className={styles.container}>
      <span className={styles.label}>{label}</span>
      <div className={styles.chipRow}>
        {allOptions.map((option) => {
          const isSelected =
            selectedValue !== undefined &&
            String(selectedValue) === String(option);
          const isAvailable = availableSet.has(String(option));

          const chipClasses = [
            "text-capitalize",
            styles.chip,
            isSelected ? styles.selected : "",
            // Unavailable options show dashed style but are still clickable
            !isAvailable && !isSelected ? styles.unavailable : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              id={`variant-chip-${String(option)}`}
              key={String(option)}
              type="button"
              className={chipClasses}
              onClick={() => onSelect(option, isAvailable)}
              aria-pressed={isSelected}
              data-testid={`variant-chip-${String(option)}`}
            >
              {String(option)}
            </button>
          );
        })}
      </div>
    </div>
  );
};
